import React from 'react';
import { Link } from 'react-router-dom';

function brokerLabel(item) {
  return [
    item.displayName || item.broker,
    item.externalAccountLabel || item.externalAccountId,
    item.paperOrLive === 'live' ? 'Live' : 'Paper',
  ].filter(Boolean).join(' · ');
}

function numericInputValue(value) {
  return value === null || value === undefined ? '' : value;
}

export default function TradeAuthorizationCard({
  details,
  isPending,
  submitting,
  canApprove,
  canConfirmLogin,
  approveLabel,
  onApprove,
  onConfirmLogin,
  statusMessage,
  brokerOptions = [],
  selectedBrokerCredentialId,
  onBrokerChange,
  addBrokerPath,
  tradeDraft = {},
  onTradeChange,
  children,
}) {
  const orderType = String(tradeDraft.orderType || 'market').toLowerCase();
  const editable = isPending && typeof onTradeChange === 'function';
  const setNumber = (field, rawValue) => {
    onTradeChange(field, rawValue === '' ? undefined : Number(rawValue));
  };

  return (
    <div
      className="action-auth-request action-auth-trade-card"
      aria-label="Request summary"
      data-testid="trade-authorization-card"
    >
      <div className="action-auth-request-wide action-auth-trade-main">
        <span className={`action-auth-trade-side side-${details.sideClass}`}>
          {details.side}
        </span>
        <strong>{details.quantity} {details.symbol}</strong>
        <p>{details.orderLabel}</p>
      </div>

      {statusMessage && (
        <div className="action-auth-request-wide action-auth-trade-status" role="status">
          <strong>{statusMessage}</strong>
        </div>
      )}

      {editable && (
        <div className="action-auth-request-wide action-auth-trade-editor" aria-label="Editable trade details">
          <div className="action-auth-field-head">
            <label htmlFor="tradeBrokerCredentialId">Broker account</label>
            {addBrokerPath && <Link className="action-auth-inline-link" to={addBrokerPath}>Add broker</Link>}
          </div>
          <select
            id="tradeBrokerCredentialId"
            value={selectedBrokerCredentialId || ''}
            disabled={submitting}
            onChange={(event) => onBrokerChange(event.target.value)}
          >
            <option value="">Choose a saved broker connection</option>
            {brokerOptions.map((item) => (
              <option key={item.id} value={item.id}>{brokerLabel(item)}</option>
            ))}
          </select>
          {brokerOptions.length === 0 && (
            <p className="action-auth-field-help">
              No saved broker connections yet. Add one, then return to approve this trade.
            </p>
          )}

          <div className="action-auth-trade-editor-grid">
            <label>
              <span>Quantity</span>
              <input
                aria-label="Trade quantity"
                min="0.00000001"
                step="any"
                type="number"
                value={numericInputValue(tradeDraft.quantity)}
                disabled={submitting}
                onChange={(event) => setNumber('quantity', event.target.value)}
              />
            </label>
            <label>
              <span>Order type</span>
              <select
                aria-label="Trade order type"
                value={orderType}
                disabled={submitting}
                onChange={(event) => onTradeChange('orderType', event.target.value)}
              >
                <option value="market">Market</option>
                <option value="limit">Limit</option>
                <option value="stop">Stop</option>
                <option value="stop_limit">Stop limit</option>
              </select>
            </label>
            <label>
              <span>Time in force</span>
              <select
                aria-label="Trade time in force"
                value={String(tradeDraft.timeInForce || 'day').toLowerCase()}
                disabled={submitting}
                onChange={(event) => onTradeChange('timeInForce', event.target.value)}
              >
                <option value="day">Day</option>
                <option value="gtc">GTC</option>
              </select>
            </label>
            {(orderType === 'limit' || orderType === 'stop_limit') && (
              <label>
                <span>Limit price</span>
                <input
                  aria-label="Trade limit price"
                  min="0.00000001"
                  step="any"
                  type="number"
                  value={numericInputValue(tradeDraft.limitPrice)}
                  disabled={submitting}
                  onChange={(event) => setNumber('limitPrice', event.target.value)}
                />
              </label>
            )}
            {(orderType === 'stop' || orderType === 'stop_limit') && (
              <label>
                <span>Stop price</span>
                <input
                  aria-label="Trade stop price"
                  min="0.00000001"
                  step="any"
                  type="number"
                  value={numericInputValue(tradeDraft.stopPrice)}
                  disabled={submitting}
                  onChange={(event) => setNumber('stopPrice', event.target.value)}
                />
              </label>
            )}
          </div>
        </div>
      )}

      <div className="action-auth-request-wide action-auth-trade-meta">
        {(details.detailRows || []).map((item) => (
          <div key={`${item.label}-${item.value}`}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>

      {details.analysisEvidence?.length > 0 && (
        <div
          className="action-auth-request-wide action-auth-trade-evidence"
          aria-label="Resolved analysis evidence"
        >
          <span>Resolved from analysis</span>
          {details.analysisEvidence.map((evidence) => (
            <div key={evidence.resolutionId || `${evidence.label}-${evidence.target}`}>
              <strong>{evidence.label}</strong>
              <small>{evidence.value}</small>
              <small>Data as of {evidence.dataTimestamp}</small>
            </div>
          ))}
        </div>
      )}

      {details.legs?.length > 0 && (
        <div className="action-auth-request-wide action-auth-trade-legs">
          <span>{details.entryLabel || 'Legs'}</span>
          {details.legs.map((leg, index) => (
            <strong key={`${index}-${leg}`}>{leg}</strong>
          ))}
        </div>
      )}

      {details.exitRows?.length > 0 && (
        <div className="action-auth-request-wide action-auth-trade-exits">
          <span>Exits</span>
          {details.exitRows.map((exit) => (
            <div key={exit.label}>
              <small>{exit.label}</small>
              <strong>{exit.value}</strong>
            </div>
          ))}
        </div>
      )}

      {children}

      {isPending && (
        <div className="action-auth-request-wide action-auth-approval">
          <div className="action-auth-actions">
            {canConfirmLogin ? (
              <button
                className="action-auth-primary"
                disabled={submitting}
                type="button"
                onClick={onConfirmLogin}
              >
                Confirm login
              </button>
            ) : (
              <button
                className="action-auth-primary"
                disabled={!canApprove}
                type="button"
                onClick={onApprove}
              >
                {approveLabel}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
